import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalendarDaysIcon, InboxIcon, UsersIcon } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { EmptyState } from '../../components/ui/Panels';
import { Avatar } from '../../components/ui/Avatar';
import { Badge } from '../../components/ui/Badge';
import { ButtonLink } from '../../components/ui/Button';
import { ThreadList } from '../../components/chat/ThreadList';
import { MessageBubble } from '../../components/chat/MessageBubble';
import { Composer } from '../../components/chat/Composer';
import { AllergyPin } from '../../components/chat/AllergyPin';
import { QuoteBuilder } from '../../components/chat/QuoteBuilder';
import { useSession } from '../../contexts/SessionContext';
import { useViewerRole } from '../../hooks/useViewerRole';
import { useAppData } from '../../contexts/AppDataContext';
import { chefById } from '../../data/chefs';
import { counterpartById } from '../../data/counterparts';
import { longDate, responseTime } from '../../utils/format';
import { useLocale } from '../../contexts/LocaleContext';
export function Inbox() {
  const {
    threadId
  } = useParams();
  const {
    chef: myChef,
    customer: me
  } = useSession();
  const {
    threads,
    sendMessage,
    sendQuote,
    markRead,
    bookingById,
    quoteById
  } = useAppData();
  const {
    locale
  } = useLocale();
  const [builderOpen, setBuilderOpen] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const role = useViewerRole();
  const basePath = role === 'chef' ? '/chef/inbox' : '/app/inbox';
  const mine = useMemo(() => threads.filter(t => role === 'chef' ? t.chefId === myChef.id : t.customerId === me.id).sort((a, b) => b.lastAt.localeCompare(a.lastAt)), [threads, role, myChef.id, me.id]);
  const active = mine.find(t => t.id === threadId) ?? (threadId ? undefined : mine[0]);
  useEffect(() => {
    if (active) markRead(active.id, role);
  }, [active?.id, markRead, role]);
  useEffect(() => {
    endRef.current?.scrollIntoView({
      block: 'end'
    });
  }, [active?.messages.length, active?.id]);
  const other = active ? role === 'chef' ? counterpartById(active.customerId) : chefById(active.chefId) : undefined;
  const booking = active ? bookingById(active.bookingId) : undefined;
  const quoteChef = active ? chefById(active.chefId) : undefined;
  const latestQuote = booking?.quoteId ? quoteById(booking.quoteId) : undefined;
  if (!mine.length) {
    return <AppShell title="Inbox" data-id="element-2411" data-mp="1x38hmuvxmxis">
        <EmptyState icon={<InboxIcon className="h-5 w-5" aria-hidden="true" data-id="element-2413" data-mp="1t24ynzil8ldc" />} title="No conversations yet" body={role === 'chef' ? 'Requests from guests will land here.' : 'Request a quote from a chef and the conversation starts here.'} action={role === 'chef' ? undefined : <ButtonLink to="/discover" data-id="element-2414" data-mp="1e5xf91f07nz4">Find a chef</ButtonLink>} data-id="element-2412" data-mp="1t2okvsf9tvx6" />
      </AppShell>;
  }
  return <AppShell title="Inbox" subtitle={`${mine.length} conversations`} flush wide data-id="element-2415" data-mp="1428rh8vwxmgu">
      <div className="grid h-[calc(100vh-4.25rem)] grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)]" data-id="element-2416" data-mp="190jxf8j37cts">
        <div className={`overflow-y-auto border-r border-hairline bg-surface lg:block ${threadId ? 'hidden' : 'block'}`} data-id="element-2417" data-mp="1e06czome155r">
          <ThreadList threads={mine} role={role} basePath={basePath} data-id="element-2418" data-mp="1ixoptq3j04j1" />
        </div>

        {!active ? <div className="p-6" data-id="element-2419" data-mp="lcsngbl0rbvo">
            <EmptyState icon={<InboxIcon className="h-5 w-5" aria-hidden="true" data-id="element-2421" data-mp="q9j2cvl381fi" />} title="Conversation not found" body="It may have been archived." action={<ButtonLink to={basePath} data-id="element-2422" data-mp="1005ak4qh5po6">Back to inbox</ButtonLink>} data-id="element-2420" data-mp="qabzr5y5pekz" />
          </div> : <section className={`min-w-0 flex-col lg:flex ${threadId ? 'flex' : 'hidden'}`} aria-label={`Conversation with ${other?.name}`} data-id="element-2423" data-mp="2kzswcqaxilu">
            <header className="flex flex-wrap items-center gap-3 border-b border-hairline bg-surface px-4 py-3" data-id="element-2424" data-mp="7hq70uijq44m">
              <Avatar name={other?.name ?? 'Guest'} src={(other as {
            photo?: string;
          })?.photo} size="sm" data-id="element-2425" data-mp="249xbzdkfjjby" />
              <div className="min-w-0 flex-1" data-id="element-2426" data-mp="2iyjoz8ml3z00">
                <p className="truncate text-sm font-semibold text-ink" data-id="element-2427" data-mp="2izlak71883vz">
                  {role === 'customer' && quoteChef ? <Link to={`/chefs/${quoteChef.slug}`} className="hover:text-primary" data-id="element-2428" data-mp="2e6a2y3olbj30">
                      {other?.name}
                    </Link> : other?.name}
                </p>
                <p className="truncate text-[12px] text-ink-3" data-id="element-2429" data-mp="31xxkvw3vjjt5">
                  {active.subject}
                  {role === 'customer' && quoteChef ? ` · replies in ${responseTime(quoteChef.responseMinutes)}` : ''}
                </p>
              </div>
              {booking && <Link to={role === 'chef' ? `/chef/bookings/${booking.id}` : `/app/bookings/${booking.id}`} className="flex items-center gap-1.5 rounded-lg border border-hairline px-2.5 py-1.5 text-[12px] font-medium text-ink-2 hover:text-ink" data-id="element-2430" data-mp="12l38xjwy97tf">
                  <CalendarDaysIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2431" data-mp="34inb1wp68ktj" />
                  {booking.ref}
                </Link>}
            </header>

            {booking && <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-hairline bg-oat px-4 py-2 text-[12px] text-ink-2" data-id="element-2432" data-mp="2vgw4yqqpybz5">
                <span className="flex items-center gap-1.5" data-id="element-2433" data-mp="30htl6xs64vy0">
                  <CalendarDaysIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2434" data-mp="2lk196fltypok" />
                  {longDate(booking.date, locale)} at {booking.time}
                </span>
                <span className="flex items-center gap-1.5" data-id="element-2435" data-mp="jrv3d67l4mia">
                  <UsersIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2436" data-mp="jso49qjv4hsi" />
                  {booking.guests} guests
                </span>
                <span data-id="element-2437" data-mp="n9ivl1wixkrc">{booking.fulfillment === 'in_home' ? 'Cooking at the guest’s place' : 'Cook and deliver'}</span>
                <Badge tone={booking.status === 'confirmed' ? 'success' : booking.status === 'quoted' ? 'accent' : 'neutral'} data-id="element-2438" data-mp="ia676cq085mn">
                  {booking.status.replace('_', ' ')}
                </Badge>
              </div>}

            <div className="flex-1 space-y-4 overflow-y-auto px-4 py-5" data-id="element-2439" data-mp="9blxu26c38b3">
              {active.messages.map(message => <MessageBubble key={message.id} message={message} viewerRole={role} data-id="element-2440" data-mp="1624ll5kr2h40" />)}
              <div ref={endRef} data-id="element-2441" data-mp="113jnpcsz09iq" />
            </div>

            <footer className="space-y-2.5 border-t border-hairline bg-surface px-4 py-3" data-id="element-2442" data-mp="w95yjs4wiymi">
              <AllergyPin acknowledged={!!latestQuote?.allergiesAcknowledged} data-id="element-2443" data-mp="s7u6lfyk21b5" />
              <Composer onSend={body => sendMessage(active.id, body, role)} onOpenQuote={role === 'chef' ? () => setBuilderOpen(true) : undefined} placeholder={role === 'chef' ? 'Reply to the guest…' : 'Ask the chef anything…'} data-id="element-2444" data-mp="1orbbbcjyo8zb" />
            </footer>
          </section>}
      </div>

      {role === 'chef' && active && <QuoteBuilder chef={myChef} open={builderOpen} onClose={() => setBuilderOpen(false)} onSend={draft => sendQuote(myChef, active.id, draft)} seed={{
      guests: booking?.guests,
      date: booking?.date,
      time: booking?.time,
      fulfillment: booking?.fulfillment
    }} data-id="element-2445" data-mp="3bnrbmrz6hrpw" />}
    </AppShell>;
}